/** @format */
// 创建、修改事件告警Drawer - 选择事件Dialog
import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { _$, Radio, Button, Icon, Dialog, Tabs, Tab, Table } from 'kpc-react'
import { EventDialogProps } from './constants'
import { MonitorSearch } from 'src/components'

const EventDialog: React.FC<EventDialogProps> = ({
  className,
  productType,
  eventList,
  defaultRadio,
  defaultValue,
  onChange
}) => {
  const [radioValue, setRadioValue] = useState<number>(defaultRadio || undefined)
  const [optionalVisible, setOptionalVisible] = useState<boolean>(false)
  const [dialogTabs, setDialogTabs] = useState<string>('all')
  const [eventAllData, setEventAllData] = useState([]) // 全部事件
  const [eventTableData, setEventTableData] = useState([]) // 筛选后的事件
  const [checkedKeys, setCheckedKeys] = useState<string[]>([]) // 勾选的事件
  const [selectedEventData, setSelectedEventData] = useState([]) // 已选事件data
  const [query, setQuery] = useState<string>('') // 搜索
  const [tableFilter, setTableFilter] = useState({ eventLevel: '', eventTypeDesc: '' })

  // 关闭dialog
  const dialogClose = () => {
    setOptionalVisible(false)
    const keys = selectedEventData.map(item => item.name)
    onChange && onChange(radioValue, keys)
  }

  // 切换radio
  const radioValueChange = radio => {
    setRadioValue(radio)
    const keys = selectedEventData.map(item => item.name)
    onChange && onChange(radio, keys)
  }

  // 过滤eventList中不是当前prodouctType的事件
  useEffect(() => {
    if (eventList?.length && productType !== undefined) {
      // 查找productType下的事件
      const productEventList = eventList.filter(item => item.productType === productType)
      setEventAllData(productEventList)
      setEventTableData(productEventList)
      setQuery('')
    }
  }, [eventList, productType])

  useEffect(() => {
    if (eventAllData?.length) {
      // 过滤掉不可选的值
      const filterSelected = defaultValue
        .map(item => {
          return eventAllData.find(({ name }) => name === item)
        })
        .filter(item => !!item)
      setSelectedEventData(filterSelected)
      setRadioValue(defaultRadio || (filterSelected?.length ? 2 : 1))
    }
  }, [defaultValue, eventAllData])

  // 筛选
  useEffect(() => {
    if (eventAllData.length) {
      const { eventLevel, eventTypeDesc } = tableFilter
      const filterTable = eventAllData.filter(item => {
        if (!!query && !item.name.includes(query) && !item.nameDesc.includes(query)) {
          return false
        }
        if (!!eventLevel && item.eventLevel !== eventLevel) {
          return false
        }
        if (!!eventTypeDesc && item.eventTypeDesc !== eventTypeDesc) {
          return false
        }
        return true
      })
      setEventTableData(filterTable)
    }
  }, [query, tableFilter.eventLevel, tableFilter.eventTypeDesc])

  const _getEventTableGroup = item => {
    const hash = []
    const filter = eventAllData.reduce((pre, cur) => {
      if (!hash.includes(cur[item])) {
        pre.push({ label: cur[item], value: cur[item] })
        hash.push(cur[item])
      }
      return pre
    }, [])
    return filter
  }

  // eventList scheme
  const eventListScheme = {
    nameDesc: {
      title: _$('事件名称'),
      width: 280
    },
    eventLevel: {
      title: _$('事件等级'),
      width: 150,
      group: [{ label: _$('全部'), value: '' }, ..._getEventTableGroup('eventLevel')]
    },
    eventTypeDesc: {
      title: _$('事件类型'),
      width: 300,
      group: [{ label: _$('全部'), value: '' }, ..._getEventTableGroup('eventTypeDesc')]
    },
    active: {
      title: _$('操作'),
      width: 80,
      template: item => {
        const find = selectedEventData.find(({ name }) => item.name === name)
        return find ? (
          <span style={{ color: '#999' }}>{_$('已添加')}</span>
        ) : (
          <a
            className="primary-color"
            onClick={() => {
              setSelectedEventData([...selectedEventData, item])
            }}
          >
            {_$('添加')}
          </a>
        )
      }
    }
  }

  const unbindEvent = item => {
    const update = selectedEventData?.filter(({ name }) => name !== item.name)
    setSelectedEventData(update)
  }

  const selectedScheme = {
    nameDesc: {
      title: _$('事件名称'),
      width: 320
    },
    eventLevel: {
      title: _$('事件等级'),
      width: 120
    },
    eventTypeDesc: {
      title: _$('事件类型'),
      width: 320
    },
    active: {
      title: _$('操作'),
      width: 60,
      template: item => {
        return (
          <a className="primary-color" onClick={() => unbindEvent(item)}>
            {_$('解绑')}
          </a>
        )
      }
    }
  }

  const addEvent = () => {
    if (eventTableData?.length && checkedKeys?.length) {
      const update = checkedKeys.map(item => {
        return eventTableData.find(({ nameDesc }) => item === nameDesc)
      })
      const filter = update.filter(item => {
        const name = item?.nameDesc
        if (!name) {
          return false
        } else {
          const find = selectedEventData.find(({ nameDesc }) => nameDesc === name)
          return !find
        }
      })
      setSelectedEventData([...selectedEventData, ...filter])
      setCheckedKeys([])
    }
  }

  return (
    <div className={className}>
      <div className="event-radio">
        <Radio name="instanceRadio" trueValue={1} value={radioValue} onChange={radioValueChange}>
          {_$('全部事件')}
        </Radio>
        <Radio name="instanceRadio" trueValue={2} value={radioValue} onChange={radioValueChange}>
          {_$('自选事件')}
          {radioValue === 2 ? (
            <span>
              (<span className="primaryicon">{selectedEventData?.length || 0}</span>)
            </span>
          ) : null}
        </Radio>
      </div>
      <div className="event-body">
        {/* 全部事件 */}
        {radioValue === 1 && <p>{_$('您已选择全部事件。')}</p>}
        {/* 自选事件 */}
        {radioValue === 2 && (
          <>
            <Button
              type="primary"
              onClick={() => {
                setOptionalVisible(true)
              }}
            >
              <Icon className="ion-plus-round" size="small" />
              {_$('添加事件')}
            </Button>

            {/* 自选事件dialog */}
            <Dialog
              width={900}
              value={optionalVisible}
              on$change-value={(c, show) => {
                !show && dialogClose()
              }}
              title={_$('自选事件')}
              b-footer={
                <>
                  <Button type="primary" onClick={() => setOptionalVisible(false)}>
                    {_$('关闭')}
                  </Button>
                </>
              }
            >
              <div className="event-dialog-body">
                <Tabs value={dialogTabs} on$change-value={(c, tab) => setDialogTabs(tab)}>
                  <Tab value="all">{_$('全部事件')}</Tab>
                  <Tab value="selected">
                    {_$('已选事件')}(
                    <span className="primaryicon">{selectedEventData?.length || 0}</span>)
                  </Tab>
                </Tabs>
                {dialogTabs === 'all' && (
                  <>
                    <div className="marginTop">
                      <Button
                        type="primary"
                        style={{ marginRight: '16px' }}
                        disabled={!checkedKeys.length}
                        onClick={addEvent}
                      >
                        {_$('批量添加')}
                      </Button>
                      <MonitorSearch
                        placeholder={_$('请输入事件名称')}
                        value={query}
                        onChange={value => {
                          setQuery(value)
                        }}
                      />
                    </div>
                    <div className="marginTop">
                      <Table
                        scheme={eventListScheme}
                        data={eventTableData}
                        rowKey={({ nameDesc }) => nameDesc}
                        on$change-checked={(_c, item) => {
                          setCheckedKeys(item)
                        }}
                        checkedKeys={checkedKeys}
                        rowCheckable={false}
                        disableRow={({ nameDesc }) => {
                          const find = selectedEventData.find(item => item.nameDesc === nameDesc)
                          return !!find
                        }}
                        group={{
                          eventLevel: tableFilter.eventLevel,
                          eventTypeDesc: tableFilter.eventTypeDesc
                        }}
                        on$change-group={(_c, group) => {
                          setTableFilter({ ...tableFilter, ...group })
                        }}
                      />
                    </div>
                  </>
                )}
                {dialogTabs === 'selected' && (
                  <>
                    <Button
                      type="primary"
                      className="marginTop"
                      onClick={() => setSelectedEventData([])}
                    >
                      {_$('全部解绑')}
                    </Button>
                    <Table
                      className="marginTop"
                      scheme={selectedScheme}
                      checkType="none"
                      data={selectedEventData}
                    ></Table>
                  </>
                )}
              </div>
            </Dialog>
          </>
        )}
      </div>
    </div>
  )
}

export default styled(EventDialog)`
  margin-top: 6px;
  .event-radio {
    .k-radio {
      margin-right: 16px;
    }
  }
  .event-body {
    margin-top: 16px;
  }
`
